import { useEffect, useMemo, useState } from "react";

export type Mode = "na" | "val";

export const isNA = (v: any) =>
  typeof v === "string" && v.trim().toUpperCase() === "N/A";

export const normalizeReadOnly = (readOnly: any): boolean =>
  typeof readOnly === "string" ? readOnly === "true" : !!readOnly;

type UseNAStateArgs = {
  value: any;
  nullable: boolean;
};


export const useNAState = ({ value, nullable }: UseNAStateArgs) => {
  const initialValue: string = useMemo(() => {
    if (!nullable && isNA(value)) return "";
    return value ?? "";
  }, [value, nullable]);

  const initialMode: Mode = useMemo(
    () => (nullable && isNA(initialValue) ? "na" : "val"),
    [nullable, initialValue]
  );

  const [mode, setMode] = useState<Mode>(initialMode);
  const [text, setText] = useState<string>(isNA(initialValue) ? "" : initialValue);

  useEffect(() => {
    if (!nullable) {
      setMode("val");
      setText(isNA(value) ? "" : (value ?? ""));
    } else {
      const nextMode: Mode = isNA(value) ? "na" : "val";
      setMode(nextMode);
      setText(isNA(value) ? "" : (value ?? ""));
    }
  }, [value, nullable]);

  return { mode, setMode, text, setText };
};

type UseDisabledArgs = {
  readOnly: any;
  nullable: boolean;
  mode: Mode;
};

export const useDisabled = ({ readOnly, nullable, mode }: UseDisabledArgs) => {
  const ro = normalizeReadOnly(readOnly);
  return { ro, disabled: ro || (nullable && mode === "na") };
};

export const isEmptyValue = (v: any): boolean => {
  if (v == null) return true;
  if (typeof v === "string") return v.trim() === "";
  if (Array.isArray(v)) return v.length === 0;
  return false;
};
